import { useCallback, useEffect, useMemo, useRef } from 'react';
import camelCase from 'lodash/camelCase';
import groupBy from 'lodash/groupBy';
import deepEquals from 'lodash/isEqual';
import mapKeys from 'lodash/mapKeys';
import merge from 'lodash/merge';
import {
	useLoadingStatusService,
	type LoadingStatus,
} from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	getEmptyExtensionsObject,
	SOURCE_GLOBAL_ISSUE_CREATE,
	UI_MODIFICATIONS_MODULE,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type {
	IssueAdjustmentExtension,
	UiModificationsExtension,
	CustomFieldExtension,
	AggExtension,
	DataClassificatedExtensionContexts,
	ExtensionContextsMap,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type { ExtensionPointModule } from '@atlassian/jira-forge-ui-types/src/common/types/module.tsx';
import { doesExtensionMeetConditions } from '@atlassian/jira-forge-ui-utils/src/utils/conditions/index.tsx';
import { fetchModules } from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules-v2/index.tsx';
import fetchForgeModules from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules/index.tsx';
import { filterOutDuplicatedExtensions } from '@atlassian/jira-forge-ui-utils/src/utils/filter-out-duplicate-extensions/index.tsx';
import type { ForgeContext } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { CloudId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

type Data = {
	extensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
	accessNarrowedExtensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
};
const moduleToVariableKey = (module: ExtensionPointModule) => camelCase(module);

export const transformResponseWithAccessNarrowedExtensions = (
	extensions: AggExtension[],
): DataClassificatedExtensionContexts => {
	const groupedExtensions = groupBy(extensions, 'type');
	const groupedExtensionsWithMappedKeys = merge(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		mapKeys(groupedExtensions, (_, key) => moduleToVariableKey(key as ExtensionPointModule)),
		getEmptyExtensionsObject(),
	);
	const filteredOutDuplicatedExtensions = filterOutDuplicatedExtensions(
		groupedExtensionsWithMappedKeys,
	);

	const allowedExtensions = getEmptyExtensionsObject();
	const accessNarrowedExtensions = getEmptyExtensionsObject();

	Object.entries(filteredOutDuplicatedExtensions).forEach(([key, module]) => {
		const allowedExtensionsMap = new Map();
		const accessNarrowedExtensionsMap = new Map();
		module.forEach((moduleItem) => {
			if (
				moduleItem?.dataClassificationPolicyDecision?.status === 'BLOCKED' ||
				moduleItem?.hiddenBy
			) {
				accessNarrowedExtensionsMap.set(moduleItem.id, moduleItem);
			} else {
				allowedExtensionsMap.set(moduleItem.id, moduleItem);
			}
		});
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		allowedExtensions[key as keyof ExtensionContextsMap] = [...allowedExtensionsMap.values()];
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		accessNarrowedExtensions[key as keyof ExtensionContextsMap] = [
			...accessNarrowedExtensionsMap.values(),
		];
	});

	return {
		extensions: allowedExtensions,
		accessNarrowedExtensions,
	};
};

export const fetchForgeModulesV2 = async (
	cloudId: CloudId,
	isAnonymous: boolean,
	projectKey?: ProjectKey,
): Promise<Data> => {
	const extensionsData = await fetchModules({
		cloudId,
		isAnonymous,
		context: {
			projectKey,
		},
		includeHidden: true,
		includeScopes: true,
		types: [CUSTOM_FIELD_MODULE, CUSTOM_FIELD_TYPE_MODULE, UI_MODIFICATIONS_MODULE],
		source: SOURCE_GLOBAL_ISSUE_CREATE,
	});
	const extensionsFlat = Object.values(extensionsData).flat();

	return transformResponseWithAccessNarrowedExtensions(extensionsFlat);
};

export const useFetchForgeModulesOld = (
	extraContext: ForgeContext = {},
): {
	customFields: CustomFieldExtension[];
	filteredOutModules: CustomFieldExtension[];
	issueAdjustmentModules: IssueAdjustmentExtension[];
	error: Error | undefined;
	loadingStatus: LoadingStatus;
} => {
	const { cloudId, activationId, locale } = useTenantContext();
	const modulesToFetch = useMemo(
		() => [CUSTOM_FIELD_MODULE, CUSTOM_FIELD_TYPE_MODULE, UI_MODIFICATIONS_MODULE],
		[],
	);

	const classificationTags = extraContext?.classificationTags;
	const previousDataClassificationTags = useRef<string[]>([]);
	const dataClassificationTags = useMemo(() => {
		// When classification tags are not defined, it means the project in GIC is not loaded yet
		if (!classificationTags) {
			return undefined;
		}

		// The `classificationTags` is not referencially stable, so we need to deep compare it and
		// make sure we are only changing this `useMemo` reference when there are values changes
		if (!deepEquals(previousDataClassificationTags.current, classificationTags)) {
			previousDataClassificationTags.current = classificationTags;
		}

		return previousDataClassificationTags.current;
	}, [classificationTags]);
	const hasDataClassificationTags = !!dataClassificationTags;

	const request = useCallback(() => {
		const contextIds = [`ari:cloud:jira:${cloudId}:workspace/${activationId}`];
		return fetchForgeModules(
			cloudId,
			modulesToFetch,
			{ tags: dataClassificationTags || [] },
			{ include: { scopes: true } },
			SOURCE_GLOBAL_ISSUE_CREATE,
			contextIds,
			locale,
		);
	}, [dataClassificationTags, cloudId, activationId, modulesToFetch, locale]);

	const { data, error, fetch, loadingStatus } = useLoadingStatusService<Data>(request, {
		loading: true,
	});

	useEffect(() => {
		// Since access narrowing, we need to know if the selected project in GIC has or not data
		// classification tags in it, and make sure we call cs-extensions with those.
		//
		// So if data classification tags are not yet loaded, the request should not be invoked
		if (!hasDataClassificationTags) return;

		fetch();
	}, [fetch, hasDataClassificationTags]);

	return useMemo(() => {
		const customFields: Array<CustomFieldExtension> = [];
		const issueAdjustmentModules: Array<IssueAdjustmentExtension> = [];
		const filteredOutModules: Array<CustomFieldExtension> = [];

		const modules = data?.extensions;
		const accessNarrowedModules = data?.accessNarrowedExtensions;

		accessNarrowedModules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});
		accessNarrowedModules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});

		modules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraUiModifications?.forEach((module: UiModificationsExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				issueAdjustmentModules.push(module);
			}
		});

		return {
			customFields,
			issueAdjustmentModules,
			filteredOutModules,
			error,
			loadingStatus,
		};
	}, [data, loadingStatus, error, extraContext]);
};

export const useFetchForgeModulesNew = (
	extraContext: ForgeContext = {},
): {
	customFields: CustomFieldExtension[];
	filteredOutModules: CustomFieldExtension[];
	issueAdjustmentModules: IssueAdjustmentExtension[];
	error: Error | undefined;
	loadingStatus: LoadingStatus;
} => {
	const { cloudId, isAnonymous } = useTenantContext();

	const request = useCallback(() => {
		return fetchForgeModulesV2(cloudId, isAnonymous, extraContext.projectKey);
	}, [cloudId, isAnonymous, extraContext.projectKey]);

	const { data, error, fetch, loadingStatus } = useLoadingStatusService<Data>(request, {
		loading: true,
	});

	useEffect(() => {
		if (extraContext.projectKey) {
			fetch();
		}
	}, [fetch, extraContext.projectKey]);

	return useMemo(() => {
		const customFields: Array<CustomFieldExtension> = [];
		const issueAdjustmentModules: Array<IssueAdjustmentExtension> = [];
		const filteredOutModules: Array<CustomFieldExtension> = [];

		const modules = data?.extensions;
		const accessNarrowedModules = data?.accessNarrowedExtensions;

		accessNarrowedModules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});
		accessNarrowedModules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});

		modules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraUiModifications?.forEach((module: UiModificationsExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				issueAdjustmentModules.push(module);
			}
		});

		return {
			customFields,
			issueAdjustmentModules,
			filteredOutModules,
			error,
			loadingStatus,
		};
	}, [data, loadingStatus, error, extraContext]);
};

export const useFetchForgeModules = functionWithCondition(
	() => fg('new_graphql_endpoint_for_fetching_forge_modules'),
	useFetchForgeModulesNew,
	useFetchForgeModulesOld,
);
